/**
 * Utilities to transform condition data between v1 and v2 formats.
 * These transformations are applied on form load based on feature toggles.
 *
 * V1 structure:
 * - newDisabilities: nested follow-up objects (view:secondaryFollowUp, etc.)
 * - ratedDisabilities: includes view:selected boolean
 *
 * V2 structure:
 * - newDisabilities: flattened fields, has conditionDate, ratedDisability, and sideOfBody
 * - newDisabilities: includes entries for selected rated disabilities (increases)
 * - ratedDisabilities: no view:selected boolean
 */

import clone from 'platform/utilities/data/clone';
import {
  CONDITIONS_IPF_TRANSFORMATION_KEY,
  NEW_CONDITION_OPTION,
  RATED_DISABILITY_CONDITION,
  RATED_DISABILITY_DESCRIPTION,
} from '../constants';
import { validateApproximateDate } from '../pages/disabilityConditions/shared/utils';
import { isNewConditionsOff, isNewConditionsOn } from './index';
import {
  trackTransformToV2,
  trackTransformToV1,
  trackTransformError,
} from './tracking/features/conditionsWorkflowMigrationTracking';

export { CONDITIONS_IPF_TRANSFORMATION_KEY };

/**
 * Extract the side of body from a condition name
 * @param {string} condition - The condition name
 * @returns {object} { cleanCondition, sideOfBody }
 */
export function extractSideOfBody(condition) {
  if (!condition) return { cleanCondition: condition };

  // regex for side of body (bilateral, left, right) at end of string, case-insensitive
  const match = condition.match(/^(.+),\s*(bilateral|left|right)$/i);
  if (match) {
    const side = match[2].toUpperCase();
    return {
      cleanCondition: match[1].trim(),
      sideOfBody: side,
    };
  }

  return { cleanCondition: condition };
}

/**
 * Restore side of body suffix to condition name
 * @param {string} condition - The condition name
 * @param {string} sideOfBody - The side of body value (BILATERAL, LEFT, or RIGHT)
 * @returns {string} The condition name with appropriate suffix if applicable
 */
export function restoreSideOfBodySuffix(condition, sideOfBody) {
  if (!condition) return condition;
  if (sideOfBody && ['BILATERAL', 'LEFT', 'RIGHT'].includes(sideOfBody)) {
    return `${condition}, ${sideOfBody.toLowerCase()}`;
  }
  return condition;
}

/**
 * Transform a v1 newDisability entry to v2 format
 * @param {object} disability - The v1 disability object
 * @returns {object} The v2 disability object
 */
function transformToV2Disability(disability) {
  const { cause, condition } = disability;

  // Check if already in v2-compatible format:
  // - has ratedDisability or sideOfBody keys (v2 specific)
  // - does NOT have view:*FollowUp nested objects (v1 specific)
  const hasV2Keys =
    disability.ratedDisability !== undefined ||
    disability.sideOfBody !== undefined;
  const hasV1Keys =
    disability['view:secondaryFollowUp'] ||
    disability['view:worsenedFollowUp'] ||
    disability['view:vaFollowUp'];
  const isV2 = hasV2Keys && !hasV1Keys;

  if (isV2) {
    // Already in v2 format, return as-is for idempotency
    return { ...disability };
  }

  // Start with all existing fields to preserve unknown fields
  const transformed = { ...disability };

  // Update cause and condition
  transformed.cause = cause;
  transformed.condition = condition;

  // Extract and set sideOfBody if laterality is present in the condition name
  const { cleanCondition, sideOfBody } = extractSideOfBody(condition);
  if (sideOfBody) {
    transformed.condition = cleanCondition;
    transformed.sideOfBody = sideOfBody;
  }

  // Set ratedDisability to the placeholder used for new conditions (to distinguish from rated disabilities)
  transformed.ratedDisability = NEW_CONDITION_OPTION;

  // Restore conditionDate from view:conditionDate stashed by a previous unflatten transform (v2->v1)
  if (disability['view:conditionDate']) {
    transformed.conditionDate = disability['view:conditionDate'];
    delete transformed['view:conditionDate'];
  }

  // Flatten nested fields and remove view:* objects based on cause type
  switch (cause) {
    case 'NEW':
      if (disability.primaryDescription) {
        transformed.primaryDescription = disability.primaryDescription;
      }
      break;

    case 'SECONDARY':
      if (disability['view:secondaryFollowUp']) {
        const { causedByDisability, causedByDisabilityDescription } =
          disability['view:secondaryFollowUp'];
        if (causedByDisability) {
          transformed.causedByDisability = causedByDisability;
        }
        if (causedByDisabilityDescription) {
          transformed.causedByDisabilityDescription =
            causedByDisabilityDescription;
        }
        delete transformed['view:secondaryFollowUp'];
      }
      break;

    case 'WORSENED':
      if (disability['view:worsenedFollowUp']) {
        const { worsenedDescription, worsenedEffects } =
          disability['view:worsenedFollowUp'];
        if (worsenedDescription) {
          transformed.worsenedDescription = worsenedDescription;
        }
        if (worsenedEffects) {
          transformed.worsenedEffects = worsenedEffects;
        }
        delete transformed['view:worsenedFollowUp'];
      }
      break;

    case 'VA':
      if (disability['view:vaFollowUp']) {
        const {
          vaMistreatmentDescription,
          vaMistreatmentLocation,
          vaMistreatmentDate,
        } = disability['view:vaFollowUp'];

        if (vaMistreatmentDescription) {
          transformed.vaMistreatmentDescription = vaMistreatmentDescription;
        }

        if (vaMistreatmentLocation) {
          transformed.vaMistreatmentLocation = vaMistreatmentLocation;
        }

        if (vaMistreatmentDate) {
          // vaMistreatmentDate does not exist as a key on the v2 flow
          // stash the value so it can be restored if a subsequent unflatten transform (v2->v1) is applied
          transformed['view:vaMistreatmentDate'] = vaMistreatmentDate;

          // if vaMistreatmentDate follows the approximate date format, assign it to conditionDate.
          // Otherwise, make the value available in the vaMistreatmentDescription field so that it's still visible to the user
          const dateValidationErrors = [];
          validateApproximateDate(
            { addError: msg => dateValidationErrors.push(msg) },
            vaMistreatmentDate,
          );
          if (dateValidationErrors.length === 0) {
            transformed.conditionDate = vaMistreatmentDate;
          } else {
            transformed.vaMistreatmentDescription =
              transformed.vaMistreatmentDescription
                ? `${transformed.vaMistreatmentDescription} (${vaMistreatmentDate})`
                : vaMistreatmentDate;
          }
        }
        delete transformed['view:vaFollowUp'];
      }
      break;

    default:
      break;
  }

  return transformed;
}

/**
 * Transform a v2 newDisability entry to v1 format
 * @param {object} disability - The v2 disability object
 * @returns {object} The v1 disability object
 */
function transformToV1Disability(disability) {
  const { cause, condition, sideOfBody } = disability;

  // Check if already in v1 format:
  // - Has view:*FollowUp nested objects (v1 specific)
  const hasV1NestedObjects =
    disability['view:secondaryFollowUp'] ||
    disability['view:worsenedFollowUp'] ||
    disability['view:vaFollowUp'];

  if (hasV1NestedObjects) {
    // Already in v1 format, return as-is for idempotency
    return { ...disability };
  }

  // Start with all existing fields to preserve unknown fields
  const transformed = { ...disability };

  // Update cause and condition with side of body restored
  transformed.cause = cause;
  transformed.condition = restoreSideOfBodySuffix(condition, sideOfBody);

  // Remove v2-specific fields
  delete transformed.ratedDisability;
  delete transformed.sideOfBody;

  // Stash a copy of the conditionDate (if present) in view:conditionDate
  // The conditionDate key is not native to the v1 data model.
  // In the case of a subsequent transform to v2, this stashed copy will
  // be assigned to the conditionDate.
  if (disability.conditionDate) {
    transformed['view:conditionDate'] = disability.conditionDate;
  }
  delete transformed.conditionDate;

  // Nest fields based on cause type
  switch (cause) {
    case 'NEW':
      // primaryDescription stays at top level for NEW cause
      break;

    case 'SECONDARY': {
      const secondaryFollowUp = {};
      if (disability.causedByDisability) {
        secondaryFollowUp.causedByDisability = disability.causedByDisability;
        delete transformed.causedByDisability;
      }
      if (disability.causedByDisabilityDescription) {
        secondaryFollowUp.causedByDisabilityDescription =
          disability.causedByDisabilityDescription;
        delete transformed.causedByDisabilityDescription;
      }
      // Only add nested object if it has at least one property
      if (Object.keys(secondaryFollowUp).length > 0) {
        transformed['view:secondaryFollowUp'] = secondaryFollowUp;
      }
      break;
    }

    case 'WORSENED': {
      const worsenedFollowUp = {};
      if (disability.worsenedDescription) {
        worsenedFollowUp.worsenedDescription = disability.worsenedDescription;
        delete transformed.worsenedDescription;
      }
      if (disability.worsenedEffects) {
        worsenedFollowUp.worsenedEffects = disability.worsenedEffects;
        delete transformed.worsenedEffects;
      }
      // Only add nested object if it has at least one property
      if (Object.keys(worsenedFollowUp).length > 0) {
        transformed['view:worsenedFollowUp'] = worsenedFollowUp;
      }
      break;
    }

    case 'VA': {
      const vaFollowUp = {};
      if (disability.vaMistreatmentDescription) {
        vaFollowUp.vaMistreatmentDescription =
          disability.vaMistreatmentDescription;
        delete transformed.vaMistreatmentDescription;
      }
      if (disability.vaMistreatmentLocation) {
        vaFollowUp.vaMistreatmentLocation = disability.vaMistreatmentLocation;
        delete transformed.vaMistreatmentLocation;
      }
      // Restore vaMistreatmentDate from view:vaMistreatmentDate (stashed during flatten)
      if (disability['view:vaMistreatmentDate']) {
        vaFollowUp.vaMistreatmentDate = disability['view:vaMistreatmentDate'];
        delete transformed['view:vaMistreatmentDate'];
      }
      // Only add nested object if it has at least one property
      if (Object.keys(vaFollowUp).length > 0) {
        transformed['view:vaFollowUp'] = vaFollowUp;
      }
      break;
    }

    default:
      break;
  }

  return transformed;
}

/**
 * Create newDisability entries for selected rated disabilities
 * @param {array} ratedDisabilities - Array of rated disabilities
 * @returns {array} Array of newDisability entries for increases
 */
function createIncreaseEntries(ratedDisabilities) {
  if (!Array.isArray(ratedDisabilities)) return [];

  return ratedDisabilities
    .filter(disability => disability['view:selected'] === true)
    .map(disability => ({
      ratedDisability: disability.name,
      ratedDisabilityId: disability.ratedDisabilityId,
      cause: 'WORSENED',
      condition: RATED_DISABILITY_CONDITION,
      worsenedDescription: RATED_DISABILITY_DESCRIPTION,
      worsenedEffects: RATED_DISABILITY_DESCRIPTION,
    }));
}

/**
 * Extract rated disability increase entries from newDisabilities
 * @param {array} newDisabilities - Array of new disabilities
 * @returns {object} { selectedRatedDisabilityNames, filteredNewDisabilities }
 */
function extractIncreaseEntries(newDisabilities) {
  if (!Array.isArray(newDisabilities)) {
    return { selectedRatedDisabilityNames: [], filteredNewDisabilities: [] };
  }

  const selectedRatedDisabilityNames = [];
  const filteredNewDisabilities = [];

  newDisabilities.forEach(disability => {
    // Check if this is a rated disability increase entry
    if (
      disability.condition === RATED_DISABILITY_CONDITION &&
      disability.cause === 'WORSENED' &&
      disability.ratedDisability &&
      disability.ratedDisability !== NEW_CONDITION_OPTION
    ) {
      selectedRatedDisabilityNames.push(disability.ratedDisability);
    } else {
      filteredNewDisabilities.push(disability);
    }
  });

  return { selectedRatedDisabilityNames, filteredNewDisabilities };
}

/**
 * Remove view:selected from ratedDisabilities
 * @param {array} ratedDisabilities - Array of rated disabilities
 * @returns {array} Array without view:selected properties
 */
function cleanRatedDisabilities(ratedDisabilities) {
  if (!Array.isArray(ratedDisabilities)) return ratedDisabilities;

  return ratedDisabilities.map(disability => {
    const cleaned = { ...disability };
    delete cleaned['view:selected'];
    return cleaned;
  });
}

/**
 * Add view:selected to ratedDisabilities based on selected names
 * @param {array} ratedDisabilities - Array of rated disabilities
 * @param {array} selectedNames - Array of selected rated disability names
 * @returns {array} Array with view:selected properties added
 */
function addViewSelectedToRatedDisabilities(ratedDisabilities, selectedNames) {
  if (!Array.isArray(ratedDisabilities)) return ratedDisabilities;

  return ratedDisabilities.map(disability => ({
    ...disability,
    'view:selected': selectedNames.includes(disability.name),
  }));
}

/**
 * Wraps a transformation function with error handling.
 * On exception, emits to Datadog RUM and returns the original formData unchanged.
 *
 * @param {'flatten'|'unflatten'} type
 * @param {object} formData - Original form data (returned on error)
 * @param {Function} transform - Zero-arg function that performs the transformation
 * @returns {{ formData: object, wasTransformed: boolean }}
 */
function withTransformErrorHandling(type, formData, transform) {
  try {
    return transform();
  } catch (error) {
    trackTransformError(type, error);
    return { formData, wasTransformed: false };
  }
}

/**
 * Apply flatten transformation (v1 → v2)
 * @param {object} formData - The form data to transform
 * @returns {object} { formData, wasTransformed }
 */
export function applyFlattenTransformation(formData) {
  const beforeData = { formData };

  // Skip transformation if the formData already has the v2 indicator
  // this indicator is set on prefill and will also be set when transforming from v1->v2
  if (isNewConditionsOn(formData)) {
    return { formData, wasTransformed: false };
  }

  return withTransformErrorHandling('flatten', formData, () => {
    const transformedFormData = clone(formData);

    // Coerce newDisabilities to an array — a non-array truthy value (e.g. from
    // a corrupted saved form) would break the subsequent .map() and spread.
    transformedFormData.newDisabilities = Array.isArray(
      transformedFormData.newDisabilities,
    )
      ? transformedFormData.newDisabilities
      : [];

    // Transform existing newDisabilities
    transformedFormData.newDisabilities =
      transformedFormData.newDisabilities.map(transformToV2Disability);

    // Prune placeholder entries (neither cause nor condition is set)
    // These placeholders may be inserted as part of the UX on the v1 flow and
    // don't have a purpose in the v2 flow.
    transformedFormData.newDisabilities =
      transformedFormData.newDisabilities.filter(
        disability => disability.condition || disability.cause,
      );
    // Track count of new conditions before prepending increase entries
    const newConditionsCount = transformedFormData.newDisabilities.length;

    let increaseCount = 0;
    // Add increase entries from selected rated disabilities
    if (transformedFormData.ratedDisabilities) {
      const increaseEntries = createIncreaseEntries(
        transformedFormData.ratedDisabilities,
      );
      increaseCount = increaseEntries.length;
      if (increaseEntries.length > 0) {
        // Prepend increase entries to newDisabilities
        transformedFormData.newDisabilities = [
          ...increaseEntries,
          ...transformedFormData.newDisabilities,
        ];
      }

      // Clean ratedDisabilities (remove the view:selected property),
      // since it's not applied by the v2 flow
      transformedFormData.ratedDisabilities = cleanRatedDisabilities(
        transformedFormData.ratedDisabilities,
      );
    }
    // Ensure view:claimType is up-to-date
    // Protect against edge case where claimingNew might have been True solely
    // due to a placeholder condition inserted as part of the v1 flow
    transformedFormData['view:claimType'] = {
      'view:claimingNew': newConditionsCount > 0,
      'view:claimingIncrease': increaseCount > 0,
    };

    // Set the workflow flag to indicate v2 structure is active
    transformedFormData.disabilityCompNewConditionsWorkflow = true;

    // add a view: key that indicates a flatten transformation was applied
    transformedFormData[CONDITIONS_IPF_TRANSFORMATION_KEY] = {
      type: 'flatten',
      timestamp: new Date().toISOString(),
    };

    trackTransformToV2(beforeData, { formData: transformedFormData });

    return {
      formData: transformedFormData,
      wasTransformed: true,
    };
  });
}

/**
 * Apply unflatten transformation (v2 → v1)
 * Use case: as a rollback mechanism on IPFs that were previously flattened (v1 -> v2)
 *
 * @param {object} formData - The form data to transform
 * @returns {object} { formData, wasTransformed }
 */
export function applyUnflattenTransformation(formData) {
  const beforeData = { formData };

  // Skip transformation if the formData does not have the v2 indicator
  if (isNewConditionsOff(formData)) {
    return { formData, wasTransformed: false };
  }

  // Skip transformation if there's no indication the formData underwent the flatten process
  // (why: avoid transforming to v1 any IPFs that were started on the v2 flow)
  const transformationMarker = formData?.[CONDITIONS_IPF_TRANSFORMATION_KEY];
  if (!transformationMarker || transformationMarker.type !== 'flatten') {
    return { formData, wasTransformed: false };
  }

  return withTransformErrorHandling('unflatten', formData, () => {
    const transformedFormData = clone(formData);

    // Identify the rated disabilities and new disabilities
    // - v2 lists them all in newDisabilities
    // - v1 separates them into ratedDisabilities and newDisabilities
    const { selectedRatedDisabilityNames, filteredNewDisabilities } =
      extractIncreaseEntries(transformedFormData.newDisabilities);

    // Transform remaining newDisabilities to v1 format
    if (Array.isArray(filteredNewDisabilities)) {
      transformedFormData.newDisabilities = filteredNewDisabilities.map(
        transformToV1Disability,
      );
    }

    // Add view:selected to ratedDisabilities
    if (transformedFormData.ratedDisabilities) {
      transformedFormData.ratedDisabilities =
        addViewSelectedToRatedDisabilities(
          transformedFormData.ratedDisabilities,
          selectedRatedDisabilityNames,
        );
    }

    // Set view:claimType to reflect new conditions and increases being claimed
    transformedFormData['view:claimType'] = {
      'view:claimingNew': filteredNewDisabilities.length > 0,
      'view:claimingIncrease': selectedRatedDisabilityNames.length > 0,
    };

    // Set the workflow flag to indicate v1 structure is active
    transformedFormData.disabilityCompNewConditionsWorkflow = false;

    // Record that an unflatten transformation was applied
    transformedFormData[CONDITIONS_IPF_TRANSFORMATION_KEY] = {
      type: 'unflatten',
      timestamp: new Date().toISOString(),
    };

    trackTransformToV1(beforeData, { formData: transformedFormData });

    return {
      formData: transformedFormData,
      wasTransformed: true,
    };
  });
}

/**
 * Apply the appropriate conditions workflow transformation based on feature toggle flags.
 *
 * @param {object} formData - The current form data (from Redux store)
 * @param {object} toggles - Live feature toggle values
 * @param {boolean} toggles.transformEnabled - Whether the flatten (v1→v2) toggle is enabled
 * @param {boolean} toggles.rollbackEnabled - Whether the rollback (v2→v1) toggle is enabled
 * @returns {{ formData: object, wasTransformed: boolean }}
 *   - `wasTransformed: true`  → caller should dispatch the returned `formData`
 *   - `wasTransformed: false` → no action needed
 */
export function applyConditionsWorkflowTransformation(
  formData,
  { transformEnabled, rollbackEnabled } = {},
) {
  // Neither toggle is on — nothing to do
  if (!transformEnabled && !rollbackEnabled) {
    return { formData, wasTransformed: false };
  }

  // Both toggles on simultaneously — conflict; take no action
  if (transformEnabled && rollbackEnabled) {
    return { formData, wasTransformed: false };
  }

  if (transformEnabled) {
    // transform to the v2 format
    return applyFlattenTransformation(formData);
  }

  // transform to the v1 format
  return applyUnflattenTransformation(formData);
}
